"use client"

import { RadialMenu, type RadialMenuOption } from "@/components/ui/radial-menu"
import { playDroppodMenuClickSound } from "@/hooks/use-sound-effects"

interface RadialDroppodMenuProps {
  position: { x: number; y: number }
  money: number
  cloneCost: number
  onCloneHero: () => void
  onOpenPhaseTerminal: () => void
  onOpenUpgradeTree: () => void
  onRestart: () => void
  onClose: () => void
  isMobile?: boolean
}

// Spritesheet positions (64x64 icons): clone, restart, phase terminal, upgrade tree
const ICON_SIZE = 64
const SPRITE_URL = "/images/droppod/icons_droppod.png"

// Sprite icon component using background position
const SpriteIcon = ({ index }: { index: number }) => (
  <div
    style={{
      width: ICON_SIZE,
      height: ICON_SIZE,
      backgroundImage: `url(${SPRITE_URL})`,
      backgroundPosition: `-${index * ICON_SIZE}px 0`,
      backgroundSize: `${ICON_SIZE * 4}px ${ICON_SIZE}px`,
      imageRendering: "pixelated",
    }}
  />
)

export const RadialDroppodMenu = ({
  position,
  money,
  cloneCost,
  onCloneHero,
  onOpenPhaseTerminal,
  onOpenUpgradeTree,
  onRestart,
  onClose,
  isMobile = false,
}: RadialDroppodMenuProps) => {
  const canAffordClone = money >= cloneCost

  // Order: clone (0), restart (1), phase terminal (2), upgrade tree (3)
  const options: RadialMenuOption[] = [
    {
      id: "clone",
      icon: <SpriteIcon index={0} />,
      label: String(cloneCost),
      disabled: !canAffordClone,
      onClick: () => {
        if (canAffordClone) {
          playDroppodMenuClickSound()
          onCloneHero()
          // Don't close menu - keep droppod selected so user sees clone status update
        }
      },
      tooltip: {
        title: "Clone Hero",
        description: "Save a copy of your hero with current items, skills, and unspent skill points.",
      },
    },
    {
      id: "restart",
      icon: <SpriteIcon index={1} />,
      onClick: () => {
        playDroppodMenuClickSound()
        onRestart()
        onClose()
      },
      tooltip: {
        title: "Restart",
        description: "Restart the game from wave 1, level 1 with your current hero.",
      },
    },
    {
      id: "phase-terminal",
      icon: <SpriteIcon index={2} />,
      onClick: () => {
        playDroppodMenuClickSound()
        onOpenPhaseTerminal()
        onClose()
      },
      tooltip: {
        title: "Phase Terminal",
        description: "Access interdimensional marketplace. Items cost twice as much but may save your run.",
      },
    },
    {
      id: "upgrade-tree",
      icon: <SpriteIcon index={3} />,
      onClick: () => {
        playDroppodMenuClickSound()
        onOpenUpgradeTree()
        onClose()
      },
      tooltip: {
        title: "Upgrade Tree",
        description: "Unlock permanent upgrades for towers, hero, and economy. Persists until all clones are lost.",
      },
    },
  ]

  return (
    <div id="droppod-menu">
      <RadialMenu
        position={position}
        options={options}
        radius={70}
        iconSize={72}
        onClose={onClose}
        isMobile={isMobile}
        itemClassName="!rounded-none !border-0"
        noBorder
      />
    </div>
  )
}
